import MetadataService from "@/services/metadata";
import ErrorHandler from "@/tools/errorHandler";
import { i18n } from "@/boot/i18n";
import * as types from "./mutationTypes";
import { getMetadataDefaultState } from "./metadataState";
import { MAX_LENGTH, REQUIRED } from "../validator/methods";
import { METADATA_KIND } from "@/consts/metadata";

export default {
  initListState({ commit }) {
    commit(types.SET_METADATAS, []);
  },
  clearListState({ dispatch }) {
    dispatch("clearMetadatas");
  },
  initNewState({ commit, dispatch }, kind) {
    commit(types.SET_METADATA, getMetadataDefaultState(kind));
    const validator = validators();
    dispatch("validator/addValidator", validator.nameValidator, { root: true });
    dispatch("validator/addValidator", validator.typeValidator, { root: true });
  },
  clearNewState({ dispatch }) {
    dispatch("clearMetadata");
    dispatch("validator/clearValidator", null, { root: true });
  },
  setMetadatasSuccess({ commit }, { metadatas, total }) {
    commit(types.SET_METADATAS, metadatas);
    commit(types.SET_TOTAL_METADATAS, total);
  },
  setMetadatas({ commit }, metadatas) {
    commit(types.SET_METADATAS, metadatas);
  },
  setMetadata({ commit }, metadata) {
    commit(types.SET_METADATA, metadata);
  },
  setMetadataSuccess({ commit }, metadata) {
    commit(types.SET_METADATA, metadata);
  },
  clearMetadata({ commit }) {
    commit(types.CLEAR_METADATA);
  },
  clearMetadatas({ commit }) {
    commit(types.CLEAR_METADATAS);
    commit(types.CLEAR_TOTAL_METADATAS);
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await MetadataService.getAll(rootState.organization.organization.uuid, pagination);
      if (response.data.data.metadata) {
        dispatch("setMetadatasSuccess", {
          metadatas: response.data.data.metadata,
          total: response.data.data.total_rows
        });
      } else {
        dispatch("setMetadatasSuccess", {
          metadatas: [],
          total: 0
        });
      }
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllByType({ dispatch }, type) {
    const pagination = {};
    if (type) {
      pagination.kind = type;
    }
    await dispatch("getAll", pagination);
  },
  async delete({ rootState, dispatch }, metadataUUID) {
    try {
      await MetadataService.delete(rootState.organization.organization.uuid, metadataUUID);
      dispatch("ui/showToastSuccess", i18n.t("metadata.messages.remove_success"), { root: true });
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async save({ dispatch, state }) {
    const metadata = state.metadata;

    metadata.name = metadata.name.trim();
    const error = validateName(metadata.name);
    if (error) {
      throw error;
    }
    const isValid = await dispatch("validate");
    if (!isValid) {
      return false;
    }

    try {
      let metadataSaved = null;
      if (!metadata.uuid) {
        metadataSaved = await dispatch("create", metadata);
      } else {
        metadataSaved = await dispatch("update", metadata);
      }

      dispatch("setMetadataSuccess", metadataSaved.data.data);
      dispatch("organization/getOne", null, { root: true });
      dispatch("ui/showToastSuccess", i18n.t("metadata.messages.create_success"), { root: true });
      return true;
    } catch (error) {
      dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  create({ rootState }, data) {
    return MetadataService.create(rootState.organization.organization.uuid, data);
  },
  update({ rootState }, data) {
    return MetadataService.update(rootState.organization.organization.uuid, data);
  },
  setMetadataField({ commit, dispatch, state }, { field, value }) {
    commit(types.SET_METADATA_FIELD, { field, value });
    dispatch("validator/checkValidation", { field, value: state.metadata[field] }, { root: true });
  },
  validate({ rootState, dispatch, state }) {
    dispatch("validator/validate", { field: "name", value: state.metadata.name }, { root: true });
    dispatch("validator/validate", { field: "type", value: state.metadata.type }, { root: true });

    return rootState.validator.errors?.length === 0;
  },
  getTags({ state }, withoutBraces) {
    let feed = [];

    if (withoutBraces) {
      feed = ["customer.name", "customer.firstname", "organization.name", "survey.questions", "survey.estimatedtime"];
    } else {
      feed = [
        "{customer.name}",
        "{customer.firstname}",
        "{organization.name}",
        "{survey.questions}",
        "{survey.estimatedtime} "
      ];
    }

    let metadatas = [];
    if (state.metadatas !== null) {
      metadatas = state.metadatas;
      if (metadatas) {
        metadatas.forEach((metadata) => {
          if (metadata.kind === METADATA_KIND.CUSTOMER) {
            if (withoutBraces) {
              feed.push("customer." + metadata.name);
            } else {
              feed.push("{customer." + metadata.name + "}");
            }
          } else if (metadata.kind === METADATA_KIND.INTERACTION) {
            if (withoutBraces) {
              feed.push("interaction." + metadata.name);
            } else {
              feed.push("{interaction." + metadata.name + "}");
            }
          }
        });
      }
    }
    return feed;
  }
};

const validators = () => {
  return {
    nameValidator: {
      field: "name",
      validations: [
        { method: MAX_LENGTH, value: 64, message: i18n.t("general.rules.max_length") + " 64" },
        { method: REQUIRED, message: i18n.t("general.rules.required") }
      ],
      meta: { tabIndex: 0 }
    },
    typeValidator: {
      field: "type",
      validations: [{ method: REQUIRED, message: i18n.t("general.rules.required") }]
    }
  };
};

function validateName(name) {
  const error = {
    response: {
      data: {
        data: [
          {
            field: "name",
            msg: "Failed on: dbUnique"
          }
        ],
        success: 0,
        message: "validation failed"
      }
    }
  };

  const nameCheck = name.toLowerCase();
  if (nameCheck === "nome" || nameCheck === "nombre" || nameCheck === "name") {
    return error;
  }
  if (nameCheck === "telefone" || nameCheck === "telefono" || nameCheck === "phone") {
    return error;
  }
  if (nameCheck === "email" || nameCheck === "e-mail" || nameCheck === "correo electronico") {
    return error;
  }
  return false;
}
